import { skipToken, useQuery } from "@tanstack/react-query";
import { QueryKey, StaleTime } from "../types";
import { config } from "../../../config";
import { useGeocodingCore } from "@mapbox/search-js-react";

const { MAPBOX_ACCESS_TOKEN } = config;

/**
 * a hook that queries the mapbox endpoint to get coordinates from an address string
 * @returns coordinates as [-76.567157, 39.286619], plus isResolving, which is
 * true while the geocode for the address has not yet succeeded or failed
 */
export const useCoordinates = ({
  address,
  enabled = true,
}: {
  address?: string;
  /**
   * add `enabled: false` to keep the query from running at all, or until `refetch` is invoked
   */
  enabled?: boolean;
}) => {
  // * Geocode
  /** do not destructure `geocodingCore`.
   * Its functions must be called from the returned object directly */
  const geocodingCore = useGeocodingCore({
    accessToken: MAPBOX_ACCESS_TOKEN as string,
    country: "us",
  });

  // * Queries
  const { data, isError, isFetching, refetch } = useQuery({
    enabled: !!address && enabled,
    queryKey: [QueryKey.MapboxForward, address, geocodingCore],
    queryFn: address
      ? async () => {
          const results = await geocodingCore.forward(address);
          return results.features.length === 0
            ? null // Undefined is an illegal cache value for successful queries
            : results.features[0];
        }
      : skipToken,
    meta: { errorMessage: "Mapbox forward geocoding failed" },
    staleTime: StaleTime.Infinite,
  });

  return {
    coordinates: data?.geometry.coordinates,
    isGeocodingError: isError,
    isResolving: isFetching,
    refetch,
  };
};
